import {type ReactNode, useEffect, useState, type MouseEvent} from 'react';
import {createPortal} from 'react-dom';
import {useConfigContext} from "@/ConfigProvider";
import {useAutocomplete} from "@/autocomplete/context/GlobalContext";
import {MobileSearchField} from "@/autocomplete/components/MobileSearchField";
import { useMinimalSearchVisibility } from "@/autocomplete/hooks/useMinimalSearchVisibility";

type AutocompleteModalProps = {
    children: ReactNode;
}

function calculateHeaderHeight() {
    const headerTop = document.querySelector('.headerTop');
    const headerMain = document.querySelector('.headerMain__top');

    let totalHeight = 0;

    if (headerTop) {
        const headerTopRect = headerTop.getBoundingClientRect();
        totalHeight += headerTopRect.height;
    }

    if (headerMain) {
        const headerMainRect = headerMain.getBoundingClientRect();
        totalHeight += headerMainRect.height;
    }

    return totalHeight;
}
export function Modal({children}: AutocompleteModalProps) {
    const {isModalOpen, setIsModalOpen, setForceModalOpen, autocompleteState} = useAutocomplete();
    const {tc} = useConfigContext();
    const [headerHeight, setHeaderHeight] = useState(0);
    const showMobileSearch = useMinimalSearchVisibility();

    const isMinimalMode = showMobileSearch && (!autocompleteState.query || autocompleteState.query.trim() === '');



    const handleModalClick = (e: MouseEvent<HTMLDivElement>) => {
        if (!(e.target as HTMLElement).closest('.algoliaAutocompleteModal__box')) {
            setIsModalOpen(false);
            setForceModalOpen(false);
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
            if (!isMinimalMode) {
                document.body.classList.add('is-blurred');
            }
        } else {
            document.body.style.overflow = '';
            document.body.classList.remove('is-blurred');
        }
        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('is-blurred');
        };
    }, [isModalOpen, isMinimalMode]);

    useEffect(() => {
        const updateHeaderHeight = () => {
            const height = calculateHeaderHeight();
            setHeaderHeight(height);
        };

        updateHeaderHeight();

        window.addEventListener('resize', updateHeaderHeight);
        window.addEventListener('scroll', updateHeaderHeight);

        const headerTop = document.querySelector('.headerTop');
        const headerMain = document.querySelector('.headerMain__top');

        const resizeObserver = new ResizeObserver(() => {
            updateHeaderHeight();
        });

        if (headerTop) resizeObserver.observe(headerTop);
        if (headerMain) resizeObserver.observe(headerMain);

        const mutationObserver = new MutationObserver(() => {
            updateHeaderHeight();
        });

        if (headerTop) {
            mutationObserver.observe(headerTop, {
                attributes: true,
                attributeFilter: ['class', 'style']
            });
        }
        if (headerMain) {
            mutationObserver.observe(headerMain, {
                attributes: true,
                attributeFilter: ['class', 'style']
            });
        }

        return () => {
            window.removeEventListener('resize', updateHeaderHeight);
            window.removeEventListener('scroll', updateHeaderHeight);
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);

    const modalContent = (
        <div aria-hidden={!isModalOpen} className={`algoliaAutocompleteModal ${isModalOpen ? ' is-active' : ''} ${isMinimalMode ? ' algoliaAutocompleteModal--minimal' : ''}`} style={{top: `${headerHeight}px`}}
             onClick={(e) => handleModalClick(e)}>
            <div className="algoliaAutocompleteModal__box">
                <div className={"algoliaAutocompleteModal__content"}>
                    <button
                        className="algoliaAutocompleteModal__close"
                        style={showMobileSearch ? { top: `${headerHeight + 28}px` } : {}}
                        onClick={() => {
                            setIsModalOpen(false);
                            setForceModalOpen(false);
                        }}
                        title={tc.close}
                    >
                        <i className="icon icon-close" aria-hidden={true}></i>
                    </button>
                    <MobileSearchField headerHeight={headerHeight} />
                    {!isMinimalMode && children}
                </div>
            </div>
        </div>
    );

    const portalTarget = document.querySelector('.autocomplete__results') || document.body;
    return createPortal(modalContent, portalTarget);
}

